import { useState, useEffect } from "react";
import { useUser } from "@clerk/react";
import { TrendingUp, Heart, Filter } from "lucide-react";
import { LazyVideo } from "@/components/ui/lazy-video";
import { VideoModal } from "@/components/ui/video-modal";
import { cn } from "@/lib/utils";
import { trackViewContent } from "@/services/analytics";

const S3 = "https://animepup.s3.us-east-1.amazonaws.com/samples";

const CATEGORIES = ["All", "Surreal", "Fights", "Scenery"];

const GALLERY = [
  { title: "Melting Time", style: "Surreal", views: "15.1K", likes: "2.8K", video: `${S3}/Surreal/1.mp4` },
  { title: "Dream Shift", style: "Surreal", views: "9.8K", likes: "1.6K", video: `${S3}/Surreal/2.mp4` },
  { title: "Mirror World", style: "Surreal", views: "13.4K", likes: "2.4K", video: `${S3}/Surreal/3.mp4` },
  { title: "Gravity Bend", style: "Surreal", views: "11.2K", likes: "1.9K", video: `${S3}/Surreal/4.mp4` },
  { title: "Void Walker", style: "Surreal", views: "18.3K", likes: "3.5K", video: `${S3}/Surreal/5.mp4` },
  { title: "Fractal Gate", style: "Surreal", views: "22.1K", likes: "4.2K", video: `${S3}/Surreal/6.mp4` },
  { title: "Endless Fall", style: "Surreal", views: "16.7K", likes: "3.0K", video: `${S3}/Surreal/7.mp4` },
  { title: "Time Loop", style: "Surreal", views: "12.5K", likes: "2.1K", video: `${S3}/Surreal/8.mp4` },
  { title: "Shattered Sky", style: "Surreal", views: "20.9K", likes: "3.8K", video: `${S3}/Surreal/9.mp4` },
  { title: "Reality Tear", style: "Surreal", views: "14.6K", likes: "2.6K", video: `${S3}/Surreal/10.mp4` },
  { title: "First Strike", style: "Fights", views: "31.8K", likes: "5.1K", video: `${S3}/Fights/1.mp4` },
  { title: "Blade Clash", style: "Fights", views: "19.7K", likes: "3.5K", video: `${S3}/Fights/2.mp4` },
  { title: "Thunder Fist", style: "Fights", views: "26.4K", likes: "4.8K", video: `${S3}/Fights/3.mp4` },
  { title: "Shadow Duel", style: "Fights", views: "23.6K", likes: "4.1K", video: `${S3}/Fights/4.mp4` },
  { title: "Power Surge", style: "Fights", views: "28.2K", likes: "4.5K", video: `${S3}/Fights/5.mp4` },
  { title: "Final Blow", style: "Fights", views: "17.4K", likes: "2.9K", video: `${S3}/Fights/6.mp4` },
  { title: "Chain Combo", style: "Fights", views: "21.3K", likes: "3.7K", video: `${S3}/Fights/7.mp4` },
  { title: "Spirit Clash", style: "Fights", views: "25.1K", likes: "4.3K", video: `${S3}/Fights/8.mp4` },
  { title: "Demon Rush", style: "Fights", views: "30.5K", likes: "5.0K", video: `${S3}/Fights/9.mp4` },
  { title: "Zero Hour", style: "Fights", views: "18.9K", likes: "3.2K", video: `${S3}/Fights/10.mp4` },
  { title: "Golden Fields", style: "Scenery", views: "8.9K", likes: "1.4K", video: `${S3}/Scenery/1.mp4` },
  { title: "Mountain Dawn", style: "Scenery", views: "14.2K", likes: "2.1K", video: `${S3}/Scenery/2.mp4` },
  { title: "Quiet River", style: "Scenery", views: "11.5K", likes: "1.8K", video: `${S3}/Scenery/3.mp4` },
  { title: "Sunset Ridge", style: "Scenery", views: "16.7K", likes: "3.0K", video: `${S3}/Scenery/4.mp4` },
  { title: "Cherry Lane", style: "Scenery", views: "19.4K", likes: "3.4K", video: `${S3}/Scenery/5.mp4` },
  { title: "Moonlit Lake", style: "Scenery", views: "13.8K", likes: "2.5K", video: `${S3}/Scenery/6.mp4` },
  { title: "Cloud Temple", style: "Scenery", views: "22.6K", likes: "4.0K", video: `${S3}/Scenery/7.mp4` },
  { title: "Forest Path", style: "Scenery", views: "10.3K", likes: "1.7K", video: `${S3}/Scenery/8.mp4` },
  { title: "Starlit Coast", style: "Scenery", views: "15.9K", likes: "2.7K", video: `${S3}/Scenery/9.mp4` },
];

export function ExplorePage() {
  const { user } = useUser();
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeVideo, setActiveVideo] = useState<string | null>(null);

  useEffect(() => {
    trackViewContent("Explore", user?.id, { funnel_stage: "explore" });
  }, []);

  const filtered = activeCategory === "All"
    ? GALLERY
    : GALLERY.filter((g) => g.style === activeCategory);

  return (
    <div className="p-5 md:p-8 max-w-6xl mx-auto">
      <div className="mb-6 animate-fade-in-up">
        <h1 className="text-2xl font-extrabold text-foreground tracking-tight mb-1">Explore</h1>
        <p className="text-sm text-muted-foreground">Discover anime characters and animations from the community</p>
      </div>

      <div className="flex items-center gap-2 mb-6 overflow-x-auto no-scrollbar pb-1 animate-fade-in-up delay-100">
        <div className="size-8 rounded-xl bg-white/5 border border-white/8 flex items-center justify-center text-[#d4a574] shrink-0">
          <Filter className="size-3.5" />
        </div>
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={cn(
              "px-3.5 py-1.5 rounded-xl text-[13px] font-semibold whitespace-nowrap transition-all",
              activeCategory === cat
                ? "bg-white/10 text-[#d4a574]"
                : "text-muted-foreground hover:text-foreground hover:bg-white/5",
            )}
          >
            {cat}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 animate-fade-in delay-200">
        {filtered.map((item, i) => (
          <div
            key={i}
            onClick={() => setActiveVideo(item.video)}
            className="group rounded-2xl border border-white/6 card-glass overflow-hidden hover:border-white/12 card-hover cursor-pointer"
          >
            <div className="relative w-full aspect-square bg-white/3 overflow-hidden">
              <LazyVideo src={item.video} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/5 group-hover:bg-transparent transition-colors" />
              <span className="absolute top-2 left-2 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-black/40 backdrop-blur-sm text-white/80">
                {item.style}
              </span>
            </div>
            <div className="px-3 py-3">
              <p className="text-[13px] sm:text-[14px] text-foreground font-semibold mb-1.5">{item.title}</p>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1 text-[11px] sm:text-[12px] text-muted-foreground">
                  <TrendingUp className="size-3" />
                  {item.views}
                </span>
                <span className="flex items-center gap-1 text-[11px] sm:text-[12px] text-[#d4a574]">
                  <Heart className="size-3" />
                  {item.likes}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {activeVideo && <VideoModal src={activeVideo} onClose={() => setActiveVideo(null)} />}
    </div>
  );
}
